import {
  applyExperienceDevOverrides,
  isCurrentPartnerParticipation,
  leadCounterparty,
  participantRoleToViewerRole,
  participantStatusToJourneyStage,
  participantStatusToProcessContext,
  readExperienceDevOverrides,
  resolveExperienceContext,
  resolveExperienceScenario,
  resolveCanonicalExperienceContext,
  resolveActiveViewerRole,
  type CounterpartyInput,
  type CanonicalExperienceContext,
  type CanonicalExperienceCapability,
  type CyclePhase,
  type CycleType,
  type ExperienceContext,
  type ExperienceContextInput,
  type ExperienceScenario,
  type JourneyStage,
  type OutcomeState,
  type ParticipantRole,
  type ParticipantStatus,
  type PartnerChoice,
  type PrimaryIntent,
  type ProcessContext,
  type SexAtBirth,
  type TreatmentGoal,
  type TreatmentModality,
  type ViewerRole,
} from "../../experience";
import type {
  HomepageGender,
  HomepageGoal,
  HomepageState3D,
  HomepageStage,
} from "../homepage.types";
import type { Pathway } from "../plan/plan.types";
import { resolvePostProcedureOutcomeClinicalState } from "../../cycles/presenters/postProcedureOutcomeState";
import {
  getHomeSurfaceContract,
  type HomeSurfaceContract,
} from "./homeExperienceContracts";

export type HomeSurface =
  | "precycle-egg-freezing"
  | "precycle-embryo-freezing"
  | "precycle-sperm-freezing"
  | "concept-home"
  | "active-treatment"
  | "cycle-complete"
  | "partner"
  | "helper";

/**
 * The recorded ending of the cycle this Home is about.
 *
 * Loose strings at the boundary, like every other field here: the caller passes
 * the cycle's `outcome` straight through and this module narrows it, so a
 * schema change cannot silently widen the resolver's behaviour.
 *
 * `resultStatus` is read as well as `type` because it is the only field that can
 * express uncertain / unavailable / adverse. Nothing writes it today — the app's
 * only write path is a `PATCH` whose outcome body cannot set it — so it is
 * accepted here for when the outcome-creation endpoint is adopted, and `type`
 * carries every ending the app can currently record.
 */
export type HomeExperienceOutcomeInput = {
  type?: string | null;
  resultStatus?: string | null;
  /**
   * The *outcome's* verification, not the cycle's. Both records carry a
   * `verificationStatus`; only this one says whether this result was refuted, and
   * every other reader in the codebase uses it. Passing the cycle's meant a
   * refuted result was presented as fact.
   */
  verificationStatus?: string | null;
  /**
   * Whether the row still stands. A result retracted as `entered_in_error` was
   * entered by mistake, so it is not the result it names — a different claim
   * from one that was refuted. `resolvePostProcedureOutcomeClinicalState`
   * already treats it as unresolved; this is what lets that rule reach it.
   */
  recordStatus?: string | null;
};

export type HomeExperienceTreatmentInput = {
  isInTreatment: boolean;
  cycleType?: string | null;
  currentPhase?: string | null;
  cycleStatus?: string | null;
  /**
   * The outcome of the cycle this Home reports on.
   *
   * Usually the ended cycle's, but an open cycle can carry one too: recording a
   * result deliberately does not close the cycle, so a patient can have a
   * recorded positive beta and an open cycle at the same time. Absent on a
   * closed cycle means the ending was never recorded, which is a distinct state
   * from an ending that resolved to nothing.
   */
  outcome?: HomeExperienceOutcomeInput | null;
  hasMonitoringSummary?: boolean;
  hasSemenSummary?: boolean;
  hasEmbryologySummary?: boolean;
  hasCryoInventory?: boolean;
  hasMoneySummary?: boolean;
};

export type HomeExperiencePersonaInput = {
  gender: SexAtBirth;
  primaryIntent: PrimaryIntent;
  treatmentGoal: string | null;
  partnerChoice: PartnerChoice;
  modality?: TreatmentModality | null;
};

export type HomeExperiencePatientInput = {
  partnerId?: string | null;
  /**
   * Relationship resolver fields injected into the patient API response by
   * `dashboard-api` (#542). RAW pg-enum values with NO server-side display
   * mapping — moon maps them into its own experience vocabulary client-side
   * (see `../../experience/relationshipsMapping`). All optional / nullable, so
   * a patient with no relationship simply leaves moon's existing
   * treatment/cycle-driven derivation in place.
   */
  participantRole?: ParticipantRole | null;
  participantStatus?: ParticipantStatus | null;
  partnerConnected?: boolean;
  counterparties?: CounterpartyInput[] | null;
};

export type HomeExperienceHelperRole =
  "intended-parent" | "carrier" | "donor" | null;

export type ResolveHomeExperienceInput = {
  patient?: HomeExperiencePatientInput;
  /** Server-owned Batch 1 context; legacy inputs remain the loading fallback. */
  canonicalContext?: CanonicalExperienceContext;
  /**
   * Explicit query state prevents an absent or failed canonical projection
   * from being reinterpreted through legacy Patient fields.
   */
  canonicalContextStatus?: "loading" | "ready" | "absent" | "error";
  persona: HomeExperiencePersonaInput;
  state3D: HomepageState3D | null;
  pathway: Pathway;
  helperRole: HomeExperienceHelperRole;
  isJourneyComplete: boolean;
  treatment: HomeExperienceTreatmentInput;
  devSearch?: string;
};

/**
 * Relationship-derived signals that don't belong in `ExperienceContext` (they
 * come from the relationships model, not treatment/cycle data) but that home
 * surfaces may want to render — e.g. a "legal review" / "matching" badge, or
 * the lead counterparty a carrier / donor is matched with.
 */
export type HomeRelationship = {
  /**
   * A moon ProcessContext badge derived from `participantStatus` for statuses
   * that are processes rather than journey stages (`legal-review` → `legal`,
   * `matched` → `matching`). Null when the status maps cleanly to a journey
   * stage or there is no relationship status.
   */
  processBadge: ProcessContext | null;
  /** The lead counterparty (e.g. the intended parent), if any. */
  leadCounterparty: CounterpartyInput | null;
  /** Whether the patient has a connected partner per the relationships model. */
  partnerConnected: boolean;
};

export type HomeExperience = {
  context: ExperienceContext;
  canonicalCapability: CanonicalExperienceCapability | null;
  /**
   * A missing journey while the canonical projection is still loading is not
   * an Explore decision yet. Home uses this state to hold its loading surface
   * instead of briefly rendering a fallback or reopening onboarding.
   */
  journeyResolutionStatus: "loading" | "resolved";
  scenario: ExperienceScenario;
  surface: HomeSurface;
  contract: HomeSurfaceContract;
  relationship: HomeRelationship;
};

/**
 * These scenarios all use the existing Explore Home. A setup scenario can
 * survive only while canonical context is pending (or in a dev fixture); it
 * must never imply that an authenticated account should restart onboarding.
 */
export function usesExploreHomeScenario(scenario: ExperienceScenario): boolean {
  return (
    scenario === "setup_unknown_intent" ||
    scenario === "learn_explorer" ||
    scenario === "grow_generic"
  );
}

const TREATMENT_GOALS = new Set<TreatmentGoal>([
  "egg-freezing",
  "sperm-freezing",
  "embryo-freezing",
  "egg-preserve",
  "sperm-preserve",
  "embryo-preserve",
  "pregnancy",
  "ivf",
  "iui",
  "fet",
  "egg-donation",
  "sperm-donation",
  "surrogacy",
  "adoption",
  "exploring",
  "unknown",
]);

const PRIMARY_INTENTS = new Set<PrimaryIntent>([
  "preserve",
  "grow",
  "donate",
  "learn",
  "unknown",
]);

const CYCLE_TYPES = new Set<CycleType>([
  "ivf",
  "iui",
  "fet",
  "icsi",
  "oi",
  "egg_freezing",
  "embryo_freezing",
  "sperm_freezing",
  "unknown",
]);

/**
 * Cycle types whose ending is a pregnancy result. Preservation cycles are
 * absent deliberately: finishing is their outcome, so they have nothing to
 * report as positive, negative or missing. `"unknown"` is absent for the same
 * reason — an unidentified cycle must not be told it is missing a result.
 */
const CONCEIVING_CYCLE_TYPES = new Set<CycleType>([
  "ivf",
  "icsi",
  "fet",
  "iui",
  "oi",
]);

const CYCLE_PHASES = new Set<CyclePhase>([
  "pre_treatment",
  "protocol",
  "stimulation",
  "trigger",
  "trigger_shot",
  "egg_retrieval",
  "sperm_collection",
  "fertilization",
  "insemination",
  "embryo_development",
  "transfer",
  "luteal_phase",
  "freezing",
  "unknown",
]);

function genderToSex(
  gender: HomepageGender | HomeExperiencePersonaInput["gender"],
): SexAtBirth {
  if (gender === "female" || gender === "AFAB") return "AFAB";
  if (gender === "male" || gender === "AMAB") return "AMAB";
  return "unknown";
}

function asTreatmentGoal(
  value: string | null | undefined,
): TreatmentGoal | null {
  return value && TREATMENT_GOALS.has(value as TreatmentGoal)
    ? (value as TreatmentGoal)
    : null;
}

function homepageGoalToTreatmentGoal(goal: HomepageGoal | null): TreatmentGoal {
  switch (goal) {
    case "preserve":
      return "unknown";
    case "embryo-preserve":
      return "embryo-freezing";
    case "pregnancy":
    case "egg-donation":
    case "sperm-donation":
    case "surrogacy":
    case "adoption":
      return goal;
    default:
      return "unknown";
  }
}

function resolveTreatmentGoal(args: {
  personaGoal: string | null;
  stateGoal: HomepageGoal | null;
}): TreatmentGoal {
  const explicitGoal = asTreatmentGoal(args.personaGoal);
  if (explicitGoal) {
    if (explicitGoal === "egg-preserve") return "egg-freezing";
    if (explicitGoal === "sperm-preserve") return "sperm-freezing";
    if (explicitGoal === "embryo-preserve") return "embryo-freezing";
    return explicitGoal;
  }

  return homepageGoalToTreatmentGoal(args.stateGoal);
}

type TreatmentSignalInput = {
  isInTreatment?: boolean;
  hasActiveCycle?: boolean;
  cycleType?: string | null;
  currentPhase?: string | null;
};

function hasTreatmentSignal(treatment?: TreatmentSignalInput): boolean {
  return (
    Boolean(treatment?.isInTreatment || treatment?.hasActiveCycle) ||
    (treatment?.cycleType != null && treatment.cycleType !== "unknown") ||
    (treatment?.currentPhase != null && treatment.currentPhase !== "unknown")
  );
}

function lacksSpecificJourneyGoal(input: ExperienceContextInput): boolean {
  const treatmentGoal = input.familyBuildingPlan?.treatmentGoal ?? "unknown";
  return (
    (treatmentGoal === "unknown" || treatmentGoal === "exploring") &&
    !hasTreatmentSignal(input.treatment)
  );
}

function normalizeMissingJourneyGoalForExplore(
  input: ExperienceContextInput,
): ExperienceContextInput {
  if (!lacksSpecificJourneyGoal(input)) {
    return input;
  }

  return {
    ...input,
    familyBuildingPlan: {
      ...input.familyBuildingPlan,
      primaryIntent: "learn",
      treatmentGoal: "exploring",
    },
  };
}

function normalizeResolvedPostAuthJourneyRouting(
  context: ExperienceContext,
): ExperienceContext {
  const treatmentGoal = context.familyBuildingPlan.treatmentGoal;
  const hasSpecificGoal =
    treatmentGoal !== "unknown" && treatmentGoal !== "exploring";
  const primaryIntent = context.familyBuildingPlan.primaryIntent;
  const compatiblePrimaryIntent =
    primaryIntent === "unknown" && hasSpecificGoal
      ? resolvePrimaryIntent("unknown", treatmentGoal)
      : primaryIntent;

  if (hasSpecificGoal) {
    return {
      ...context,
      familyBuildingPlan: {
        ...context.familyBuildingPlan,
        primaryIntent: compatiblePrimaryIntent,
      },
      // A specific persisted goal is enough to choose its Home presentation.
      // This removes a compatibility-field gap without inventing a goal or a
      // treatment from the viewer's sex.
      lifecycle:
        context.lifecycle.stage === "setup"
          ? { ...context.lifecycle, stage: "planning" }
          : context.lifecycle,
    };
  }

  if (hasTreatmentSignal(context.treatment)) {
    return context;
  }

  return {
    ...context,
    familyBuildingPlan: {
      ...context.familyBuildingPlan,
      primaryIntent: "learn",
      treatmentGoal: "exploring",
    },
    lifecycle:
      context.lifecycle.stage === "setup"
        ? {
            ...context.lifecycle,
            stage: "planning",
            journeyStage: "explore",
          }
        : context.lifecycle,
  };
}

function hasResolvedFallbackJourney(
  input: ResolveHomeExperienceInput,
  baseInput: ExperienceContextInput,
): boolean {
  const role = baseInput.viewer?.role;
  const treatmentGoal =
    baseInput.familyBuildingPlan?.treatmentGoal ?? "unknown";
  const hasSpecificGoal =
    treatmentGoal !== "unknown" && treatmentGoal !== "exploring";
  const hasExplicitExploreIntent = input.persona.primaryIntent === "learn";
  const hasSpecificParticipantRole =
    role === "partner" ||
    role === "donor" ||
    role === "carrier" ||
    role === "intended-parent";

  return (
    hasSpecificGoal ||
    hasExplicitExploreIntent ||
    hasSpecificParticipantRole ||
    hasTreatmentSignal(baseInput.treatment)
  );
}

function resolvePrimaryIntent(
  intent: HomeExperiencePersonaInput["primaryIntent"],
  goal: TreatmentGoal,
): PrimaryIntent {
  if (
    goal === "egg-freezing" ||
    goal === "sperm-freezing" ||
    goal === "embryo-freezing"
  ) {
    return "preserve";
  }
  if (intent !== "unknown" && PRIMARY_INTENTS.has(intent as PrimaryIntent)) {
    return intent;
  }
  if (goal === "egg-donation" || goal === "sperm-donation") return "donate";
  if (goal === "exploring") return "learn";
  if (goal === "unknown") return "unknown";
  return "grow";
}

function stageToJourneyStage(stage: HomepageStage): JourneyStage {
  if (stage === "get-ready") return "before-treatment";
  return stage;
}

function pathwayToPartnerChoice(pathway: Pathway): PartnerChoice {
  if (pathway === "with-donor") return "using-donor";
  return pathway;
}

function resolvePartnerChoice(args: {
  personaChoice: HomeExperiencePersonaInput["partnerChoice"];
  pathway: Pathway;
}): PartnerChoice {
  if (
    args.personaChoice === "solo" ||
    args.personaChoice === "with-partner" ||
    args.personaChoice === "using-donor"
  ) {
    return args.personaChoice;
  }
  return pathwayToPartnerChoice(args.pathway);
}

function asCycleType(value: string | null | undefined): CycleType {
  return value && CYCLE_TYPES.has(value as CycleType)
    ? (value as CycleType)
    : "unknown";
}

function asCyclePhase(value: string | null | undefined): CyclePhase {
  return value && CYCLE_PHASES.has(value as CyclePhase)
    ? (value as CyclePhase)
    : "unknown";
}

/**
 * Whether an ending names a result at all.
 *
 * An outcome row with no `type` records nothing: it is how `useCompleteCycle`
 * closes a preservation cycle, and what a bare status-only close leaves behind.
 * So it is the wrong thing to read as "the patient has a result" — which matters
 * most on an open cycle, where a row that names nothing means the cycle is still
 * simply in progress.
 *
 * Deliberately the same test Cycle Detail's `hasRecordedOpenResult` makes, so
 * the two surfaces cannot disagree about whether a result exists.
 */
function namesARecordedResult(
  outcome: HomeExperienceOutcomeInput | null | undefined,
): boolean {
  return Boolean(outcome?.type);
}

/**
 * Narrow a recorded cycle ending to the experience model's `OutcomeState`.
 *
 * Until this existed, `lifecycle.outcome` was never populated, so it defaulted
 * to `"none"`, `deriveLifecycleStage` never returned `"results"`, and every
 * `results_*` scenario — with all twenty post-procedure outcome routes and the
 * cancelled/converted review surface — was reachable only through the
 * `?dev_outcome=` override. Real patients whose cycle had ended saw
 * `results_missing`.
 *
 * The clinical precedence lives in
 * `resolvePostProcedureOutcomeClinicalState` so Home and the post-procedure
 * acknowledgement flow cannot disagree about a refuted or withdrawn result.
 * This function only adapts that state to Home's missing-result distinction.
 *
 * `"converted"` is not derived: no write path records a conversion, so
 * inferring one would be a guess. It stays reachable only via dev override
 * until the data exists.
 *
 * Note the spelling trap: this model uses `"canceled"`, the API uses
 * `"cancelled"`.
 */
function asOutcomeState(args: {
  outcome: HomeExperienceOutcomeInput | null | undefined;
  cycleStatus: string | null | undefined;
  hasActiveCycle: boolean;
  cycleType: CycleType;
}): OutcomeState {
  // An open cycle has not ended — unless the patient already has a result on it.
  // Recording a result deliberately does not close the cycle, so this is a real
  // state and not stale data from an earlier row, and it is the one case where
  // an open cycle has an ending to name. `deriveLifecycleStage` reads the
  // resulting outcome the same way, which keeps the two from disagreeing.
  if (args.hasActiveCycle && !namesARecordedResult(args.outcome)) return "none";

  const { outcome } = args;
  const isClosed = args.cycleStatus === "closed";

  // A conceiving cycle that closed with no recorded ending is a real, nameable
  // state — "we don't have your result" — and has copy for it. A preservation
  // cycle is different: `useCompleteCycle` closes it with a deliberately null
  // outcome type and the display layer infers completion from the cycle type,
  // so its ending is expressed through the journey stage and goal instead.
  // Returning anything but "none" for those would send a patient who finished
  // egg freezing from `postcycle_storage` to `results_missing`, because
  // `deriveLifecycleStage` tests the outcome before the storage branch.
  const endsInAResult = CONCEIVING_CYCLE_TYPES.has(args.cycleType);
  const unrecorded: OutcomeState =
    isClosed && endsInAResult ? "missing" : "none";

  if (!outcome) return unrecorded;

  const type = outcome.type ?? null;
  const resultStatus = outcome.resultStatus ?? null;

  const clinicalState = resolvePostProcedureOutcomeClinicalState(outcome);
  if (clinicalState === "cancelled") return "canceled";
  if (clinicalState === "adverse") return "adverse";
  if (clinicalState === "pregnancy") return "positive";
  if (clinicalState === "no_pregnancy") return "negative";

  if (
    outcome.verificationStatus === "refuted" ||
    outcome.verificationStatus === "conflicting" ||
    outcome.verificationStatus === "unavailable"
  ) {
    return "uncertain";
  }

  if (resultStatus === "unavailable") return "missing";
  if (resultStatus === "uncertain") return "uncertain";

  // `type: null` is how `useCompleteCycle` closes a preservation cycle, and
  // also what a bare status-only close leaves behind. Either way no ending was
  // named, so it is treated the same as no outcome row at all.
  if (type === null) return unrecorded;

  // `"other"`, `"pending"`, and anything a newer schema adds.
  return "uncertain";
}

function resolveHomeSurface(args: {
  context: ExperienceContext;
  scenario: ExperienceScenario;
  patient?: HomeExperiencePatientInput;
  helperRole: HomeExperienceHelperRole;
  isJourneyComplete: boolean;
}): HomeSurface {
  // An egg/sperm donor mid-cycle runs a clinically identical retrieval/
  // collection cycle to the preservation pathway, so once they have an active
  // cycle they should land on the active-treatment surface rather than the
  // static helper hero. Carriers have no gamete cycle of their own, so they
  // stay on the helper surface throughout.
  const donorInActiveCycle =
    args.helperRole === "donor" && args.context.treatment.hasActiveCycle;
  if (
    (args.helperRole === "donor" || args.helperRole === "carrier") &&
    !donorInActiveCycle
  ) {
    return "helper";
  }
  if (
    isCurrentPartnerParticipation(
      args.patient?.participantRole,
      args.patient?.participantStatus,
    )
  ) {
    return "partner";
  }
  if (args.isJourneyComplete) return "cycle-complete";
  // The cycle dashboard is the wrong surface once there is a result to report,
  // even though the cycle is still open — a recorded result has to be the first
  // thing the patient sees. The result surfaces read the same lifecycle stage, so
  // this cannot send a patient to a Home that has nothing to say.
  if (
    args.context.treatment.hasActiveCycle &&
    args.context.lifecycle.stage !== "results"
  ) {
    return "active-treatment";
  }
  if (
    args.scenario === "planning_ovarian_preservation" &&
    args.context.familyBuildingPlan.treatmentGoal === "egg-freezing" &&
    args.context.viewer.role === "primary"
  ) {
    return "precycle-egg-freezing";
  }
  if (
    args.scenario === "planning_embryo_creation" &&
    args.context.familyBuildingPlan.treatmentGoal === "embryo-freezing" &&
    args.context.viewer.role === "primary"
  ) {
    return "precycle-embryo-freezing";
  }
  if (
    (args.scenario === "planning_sperm_preservation" ||
      args.scenario === "waiting_semen_results" ||
      args.scenario === "results_missing") &&
    args.context.familyBuildingPlan.treatmentGoal === "sperm-freezing" &&
    args.context.viewer.role === "primary"
  ) {
    return "precycle-sperm-freezing";
  }
  return "concept-home";
}

export function buildHomeExperienceInput(
  input: ResolveHomeExperienceInput,
): ExperienceContextInput {
  // Persona data is the canonical source for the user's identity and intent.
  // The legacy 3D helper now returns null for incomplete profiles, so never
  // use a compatibility state to turn unanswered onboarding into treatment.
  const sexAtBirth = genderToSex(input.persona.gender);
  const personaGoal = asTreatmentGoal(input.persona.treatmentGoal);
  const hasExplicitPersonaGoal =
    personaGoal !== null && personaGoal !== "unknown";
  const missingIntent =
    input.persona.primaryIntent === "unknown" &&
    !hasExplicitPersonaGoal &&
    !hasTreatmentSignal(input.treatment);

  // Known sex with no intent is an Explorer user. If sex is also missing,
  // remain in setup so onboarding/auth recovery can collect the required
  // context. Both branches deliberately bypass the legacy pregnancy default.
  const treatmentGoal = missingIntent
    ? sexAtBirth === "unknown"
      ? "unknown"
      : "exploring"
    : input.persona.primaryIntent === "learn" && !hasExplicitPersonaGoal
      ? "exploring"
      : resolveTreatmentGoal({
          personaGoal: input.persona.treatmentGoal,
          stateGoal: input.state3D?.goal ?? null,
        });
  const primaryIntent = missingIntent
    ? sexAtBirth === "unknown"
      ? "unknown"
      : "learn"
    : resolvePrimaryIntent(input.persona.primaryIntent, treatmentGoal);
  const activeHelperRole =
    input.helperRole === null
      ? null
      : resolveActiveViewerRole(treatmentGoal, input.helperRole);
  // moon's existing treatment/cycle-driven role derivation. Used as the
  // fallback when the patient has no relationships-model role.
  const derivedRole: ViewerRole =
    activeHelperRole === "donor" ||
    activeHelperRole === "carrier" ||
    activeHelperRole === "intended-parent"
      ? activeHelperRole
      : "primary";

  // The relationships model (#542) is the canonical source for who-is-who, so
  // when the patient API resolves a `participantRole` it wins over moon's
  // treatment-derived role. Absent a relationship role, keep the derived role
  // (no breakage for patients with no relationship).
  const relationshipRole = resolveActiveViewerRole(
    treatmentGoal,
    participantRoleToViewerRole(input.patient?.participantRole),
  );
  const role: ViewerRole = relationshipRole ?? derivedRole;

  // `partnerConnected` is a server summary, not something the client can
  // infer from the legacy partnerId field. Missing data is unresolved.
  const partnerConnected = input.patient?.partnerConnected ?? false;

  // `participantStatus` maps into moon's journey vocabulary client-side. A
  // resolved relationship status (e.g. a donor who is `screening`) is a
  // stronger signal of journey stage than the self-reported 3D state, so it
  // wins when present; otherwise fall back to the 3D-derived stage.
  const relationshipJourneyStage =
    relationshipRole !== null
      ? participantStatusToJourneyStage(input.patient?.participantStatus)
      : null;
  const journeyStage: JourneyStage =
    relationshipJourneyStage ??
    stageToJourneyStage(input.state3D?.stage ?? "explore");

  const cycleType = asCycleType(input.treatment.cycleType);
  const currentPhase = asCyclePhase(input.treatment.currentPhase);

  return {
    viewer: {
      role,
      sexAtBirth,
      isPrimary: role === "primary",
      partnerConnected,
    },
    familyBuildingPlan: {
      primaryIntent,
      treatmentGoal,
      partnerChoice: resolvePartnerChoice({
        personaChoice: input.persona.partnerChoice,
        pathway: input.pathway,
      }),
      modality: input.persona.modality ?? "unknown",
    },
    lifecycle: {
      journeyStage,
      // Without this the field defaults to "none" and no real patient can ever
      // reach a results scenario. See `asOutcomeState`.
      outcome: asOutcomeState({
        outcome: input.treatment.outcome,
        cycleStatus: input.treatment.cycleStatus,
        hasActiveCycle: input.treatment.isInTreatment,
        cycleType,
      }),
    },
    treatment: {
      hasActiveCycle: input.treatment.isInTreatment,
      cycleType,
      currentPhase,
      cycleStatus:
        input.treatment.cycleStatus === "open" ||
        input.treatment.cycleStatus === "closed" ||
        input.treatment.cycleStatus === "completed" ||
        input.treatment.cycleStatus === "discontinued"
          ? input.treatment.cycleStatus
          : "unknown",
    },
    metrics: {
      hasMonitoringSummary: !!input.treatment.hasMonitoringSummary,
      hasSemenSummary: !!input.treatment.hasSemenSummary,
      hasEmbryologySummary: !!input.treatment.hasEmbryologySummary,
      hasCryoInventory: !!input.treatment.hasCryoInventory,
      hasMoneySummary: !!input.treatment.hasMoneySummary,
    },
    dataAvailability: {
      profile: input.patient ? "real" : "missing",
      monitoring: input.treatment.hasMonitoringSummary ? "real" : "missing",
      semen: input.treatment.hasSemenSummary ? "real" : "missing",
      embryology: input.treatment.hasEmbryologySummary ? "real" : "missing",
      cryoInventory: input.treatment.hasCryoInventory ? "real" : "missing",
      money: input.treatment.hasMoneySummary ? "real" : "missing",
    },
  };
}

export function resolveHomeExperience(
  input: ResolveHomeExperienceInput,
): HomeExperience {
  const baseInput = buildHomeExperienceInput(input);
  const hasDevSearch = input.devSearch !== undefined;
  const journeyResolutionStatus =
    !hasDevSearch &&
    input.canonicalContextStatus === "loading" &&
    !hasResolvedFallbackJourney(input, baseInput)
      ? "loading"
      : "resolved";
  const canonicalResolution =
    input.canonicalContext &&
    input.canonicalContextStatus !== "loading" &&
    input.canonicalContextStatus !== "error"
      ? resolveCanonicalExperienceContext(input.canonicalContext, {
          outcome: baseInput.lifecycle?.outcome,
        })
      : null;
  // Dev overrides intentionally keep their existing legacy path. This makes
  // route experiments deterministic while production uses the API projection.
  const context = hasDevSearch
    ? resolveExperienceContext(
        normalizeMissingJourneyGoalForExplore(
          applyExperienceDevOverrides(
            baseInput,
            readExperienceDevOverrides(input.devSearch),
          ),
        ),
      )
    : canonicalResolution
      ? normalizeResolvedPostAuthJourneyRouting(canonicalResolution.context)
      : resolveExperienceContext(
          normalizeMissingJourneyGoalForExplore(
            applyExperienceDevOverrides(
              baseInput,
              readExperienceDevOverrides(input.devSearch),
            ),
          ),
        );
  const activeContextHelperRole =
    resolveActiveViewerRole(
      context.familyBuildingPlan.treatmentGoal,
      input.helperRole,
    ) ?? null;
  const contextHelperRole =
    activeContextHelperRole === "donor" || activeContextHelperRole === "carrier"
      ? activeContextHelperRole
      : context.viewer.role === "donor" || context.viewer.role === "carrier"
        ? context.viewer.role
        : null;
  const scenario = resolveExperienceScenario(context);
  const hasActiveRelationship =
    context.viewer.role !== "primary" && context.viewer.role !== "unknown";
  const surface = resolveHomeSurface({
    context,
    scenario,
    patient: input.patient,
    helperRole: contextHelperRole,
    isJourneyComplete: input.isJourneyComplete,
  });
  const relationship: HomeRelationship = {
    processBadge: hasActiveRelationship
      ? participantStatusToProcessContext(input.patient?.participantStatus)
      : null,
    leadCounterparty: hasActiveRelationship
      ? leadCounterparty(input.patient?.counterparties)
      : null,
    partnerConnected: context.viewer.partnerConnected,
  };
  return {
    context,
    canonicalCapability: canonicalResolution?.capability ?? null,
    journeyResolutionStatus,
    scenario,
    surface,
    contract: getHomeSurfaceContract(surface),
    relationship,
  };
}
